import React, { useState } from "react";
import {
  View,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import color from "@constants/color";
import CloseIcon from "@assets/svg/CloseIcon";
import PlusThin from "@assets/svg/PlusThin";
import { width } from "@utils/metrics";

// Presentational only — no picking/uploading/geotagging here. That all lives
// in the parent (CustomerOrderView) so the single imagePickerHelper /
// useLocationHook wiring stays in one place.
//
// Layout: 3 tiles per row, left-aligned wrap. Every tile carries a fixed
// marginRight/marginBottom gap, and the row container extends past its own
// right edge by one gap (negative marginRight) so the 3rd tile in a row never
// gets pushed onto a new line by its own trailing gap.
const GAP = 8;
// Matches the modal's content width: plasticBagInputModal is `width - 48`
// wide with 20px padding on both sides → width - 88.
const TILE_SIZE = (width - 88 - GAP * 2) / 3;

const DeliveryProofImageGrid = ({
  images = [],
  maxCount = 6,
  onAddPress,
  onRemove,
}) => {
  const [loadedIds, setLoadedIds] = useState(new Set());
  const showAddTile = images.length < maxCount;
  return (
    <View style={styles.grid}>
      {images.map((img) => (
        <View key={img.id} style={styles.tile}>
          <Image
            source={{ uri: img.url || img.localUri }}
            style={styles.tileImage}
            resizeMode="cover"
            onLoad={() => setLoadedIds((prev) => new Set([...prev, img.id]))}
          />
          {img.uploading || (img.url && !loadedIds.has(img.id)) ? (
            <View style={styles.uploadingOverlay}>
              <ActivityIndicator size="small" color={color.white} />
            </View>
          ) : null}
          <TouchableOpacity
            style={styles.removeBadge}
            onPress={() => onRemove(img.id)}
          >
            <CloseIcon width={12} height={12} fill={color.mediumBlack} />
          </TouchableOpacity>
        </View>
      ))}
      {showAddTile ? (
        <TouchableOpacity style={styles.addTile} onPress={onAddPress}>
          <PlusThin width={22} height={22} />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

export default DeliveryProofImageGrid;

const styles = StyleSheet.create({
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginRight: -GAP,
  },
  tile: {
    width: TILE_SIZE,
    height: TILE_SIZE,
    borderRadius: 8,
    marginRight: GAP,
    marginBottom: GAP,
    overflow: "hidden",
    backgroundColor: color.lightGray,
  },
  tileImage: {
    width: "100%",
    height: "100%",
  },
  uploadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: color.lighterBlack,
    alignItems: "center",
    justifyContent: "center",
  },
  removeBadge: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: color.modalBg,
    alignItems: "center",
    justifyContent: "center",
  },
  addTile: {
    width: TILE_SIZE,
    height: TILE_SIZE,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: color.black02,
    borderStyle: "dashed",
    marginRight: GAP,
    marginBottom: GAP,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: color.white,
  },
});
